import { useMutation } from '@tanstack/react-query';
import { Translator } from 'Translator';
import { fetchExperience } from 'flux/clients/experiences';
import { toggleFavorite } from 'flux/clients/favorites';
import { FavoritesContext } from 'providers/FavoritesProvider';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { generatePathnameWithLangCode, getPath } from 'utils/RouteHelper';

function useFavorites(id) {
  const { favorites, dispatch } = useContext(FavoritesContext);
  const history = useHistory();

  function onSuccess(newFavoriteState: boolean) {
    history.replace(getPath());
  }

  function onError(error: any) {
    if (error.status === 401) {
      updateFavoriteContext(false);
      const searchParams = new URLSearchParams();
      searchParams.set(
        'returnTo',
        `/favorites/add/${id}/?returnTo=${getPath()}`,
      );

      history.push({
        pathname: generatePathnameWithLangCode(
          '/signup/',
          Translator.getLanguageCode(),
        ),
        state: { modal: true },
        search: searchParams.toString(),
      });
    }
  }

  function onMutate(newFavoriteState: boolean) {
    updateFavoriteContext(newFavoriteState);
  }

  function updateFavoriteContext(newFavoriteState: boolean) {
    const newFavorites = favorites.filter(
      (favorite) => favorite && favorite.id !== id,
    );
    if (newFavoriteState) {
      newFavorites.push({ id });
    }
    dispatch({ type: 'SET', payload: { favorites: newFavorites } });
    fetchExperience(id, Translator.getLanguageCode()).then((experience) =>
      dispatch({ type: 'SET_EXPERIENCE', payload: { experience } }),
    );
  }

  const mutate = useMutation(
    async (isFavorite: boolean) => {
      await toggleFavorite(id, isFavorite);
      return isFavorite;
    },
    {
      retry: 0,
      onSuccess: onSuccess,
      onError: onError,
      onMutate: onMutate,
    },
  );

  const setAsFavorite = (isWished: boolean) => {
    mutate.mutate(isWished);
  };

  return {
    setAsFavorite,
    isFavorite: favorites.some((favorite) => favorite && favorite.id === id),
  };
}

export default useFavorites;
